import React, { useState } from 'react';
import { makeStyles, tokens, Text, Input, Button } from '@fluentui/react-components';
import SelectionBox from './../blocks/SelectionBox';
import CellItem from '../CellItem';

import BasisSelectingDialog from '../../dialogs/BasisSelectingDialog';
import ItemViewDialog from '../../dialogs/ItemViewDialog';

import pic1 from '../../../assets/img/icons/pagrindas/1.jpg';
import pic2 from '../../../assets/img/icons/pagrindas/2.jpg';
import pic3 from '../../../assets/img/icons/pagrindas/3.jpg';
import pic4 from '../../../assets/img/icons/pagrindas/4.jpg';
import pic5 from '../../../assets/img/icons/pagrindas/5.jpg';
import pic6 from '../../../assets/img/icons/pagrindas/6.jpg';
import pic7 from '../../../assets/img/icons/pagrindas/7.jpg';
import pic8 from '../../../assets/img/icons/pagrindas/8.jpg';
import genericIcon from '../../../assets/img/icons/generic.svg';


// Eksportuojama, kad ta pati vaizdų/pavadinimų sąsaja galėtų būti naudojama
// kitur (pvz. Suvestinės ir /baldai/:code puslapio medžiagų sąrašuose).
export const allBasisOptions = [
    { id: 0, image: genericIcon, title: 'Nepasirinkta' },
    { id: 1, image: pic1, title: 'Padas' },
    { id: 2, image: pic2, title: 'Cokolis' },
    { id: 3, image: pic3, title: 'Kairė iki grindų' },
    { id: 4, image: pic4, title: 'Dešinė iki grindų' },
    { id: 5, image: pic5, title: 'Be pagrindo' },
    { id: 6, image: pic6, title: 'Kojelės' },
    { id: 7, image: pic7, title: 'Plokštė (Tarp šonų)' },
    { id: 8, image: pic8, title: 'Plokštė (Per visą plotį)' },
];

const useStyles = makeStyles({
    // flex+wrap (ne grid) — SelectionBox type="column" `.boxColumn` yra fiksuoto
    // 80px pločio su justifySelf:'start' (žr. SelectionBox.jsx komentarą), tad
    // platesnės grid track'os (minmax(100px,...)) palikdavo nevienodą tuščią
    // tarpą kiekvienos celės dešinėje (žr. /admin/bugs "naudojamas grid,
    // panaikink ji" — ta pati problema kaip SelectTypeDialog/RoomShapeDialog/
    // SkirtingDialog).
    optionsGrid: { display: 'flex', flexWrap: 'wrap', gap: '10px' },
    legSection: { marginTop: tokens.spacingVerticalM },
    legTitle: { color: tokens.colorBrandForeground2 },
    legItemWrap: { maxWidth: '100%', '@media (min-width: 600px)': { maxWidth: '50%' } },
    heightRow: {
        marginTop: tokens.spacingVerticalXL, paddingLeft: tokens.spacingHorizontalXS, paddingRight: tokens.spacingHorizontalXS,
        display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM,
    },
    heightInput: { width: '100px' },
    footer: { marginTop: tokens.spacingVerticalXL, display: 'flex', justifyContent: 'flex-end', gap: tokens.spacingHorizontalS },
});

const cmAdornment = <Text size={200}>cm</Text>;

const Basis = ({ cabinets, setCabinets, basisHeight, setBasisHeight, basisType, setBasisType, onClose }) => {
  const s = useStyles();

  // Temp state — changes are only applied when "Pasirinkti" is pressed
  const [tempType, setTempType] = useState(basisType);
  const [tempLeg, setTempLeg] = useState(null);
  const [openLegSelectingDialog, setOpenLegSelectingDialog] = useState(false);
  const [openLegViewDialog, setOpenLegViewDialog] = useState(false);

  const handleHeightInputChange = (e, data) => {
    const val = parseFloat(data.value);
    if (!isNaN(val) && val >= 0) {
      setBasisHeight(val / 100);
    }
  };

  const handleOptionClick = (id) => {
    setTempType(id);
    if (id === 6 && !tempLeg) setOpenLegSelectingDialog(true);
  };

  const handleLegSelect = (legItem) => {
    setTempLeg(legItem);
  };

  const handleConfirm = () => {
    // basisType 7 ("Plokštė Tarp šonų") ir 8 ("Plokštė Per visą plotį") ANKSČIAU
    // buvo sugrupuoti su 0 (Nepasirinkta) ir 5 (Be pagrindo) — abu pastarieji
    // TIKRAI neturi jokios bazės zonos, tad height=0 jiems teisinga. Tačiau 7/8
    // TURI matomą bazės zoną (UnifiedBase.jsx type 7/8 piešia atitinkamai
    // įgilintą plokštę / pilną dėžę, abi priklauso nuo safeHeight>0 — su height=0
    // UnifiedBase net negrįžta jokios geometrijos, žr. ten "safeHeight <=
    // MIN_DIMENSION return null"). Todėl basisHeight=0 čia reiškė, kad
    // pasirinkus "Plokštė (Tarp šonų)" per šį dialogą (ne per debug/state įrankį),
    // pati plokštė IŠVIS NEPIEŠIAMA, o visualBaseHeight (SingleCabinet3D.jsx)
    // tampa 0 visur — durys/šonai atsiremia tiesiai į grindis be jokio tarpo
    // (žr. vartotojo pranešimą "dabar iš vis plokste dingo", patikrinta
    // ekrano nuotraukų/pikselių palyginimu — tas pats baldas su basisHeight
    // rankiniu būdu paliktu 0.1 rodė plokštę teisingai, o per šį dialogą
    // pasirinktas rodė nieko). Pataisyta: 7/8 dabar patenka į tą patį "else"
    // šaką kaip 1/2/3/4 — išlaiko esamą aukštį arba numato 0.1 (10cm).
    if (tempType === 0 || tempType === 5) {
      setBasisType(tempType, 0);
    } else if (tempType === 6) {
      const legH = (tempLeg && tempLeg.height) ? tempLeg.height / 1000 : 0.1;
      setBasisType(tempType, legH);
    } else {
      setBasisType(tempType, basisHeight === 0 ? 0.1 : basisHeight);
    }
    onClose();
  };

  const getLegImage = () => {
    if (!tempLeg) return pic7;
    try {
      const imgName = tempLeg.image_1 || tempLeg.image;
      return require(`../../../assets/img/legs/${imgName}`);
    } catch (e) {
      return pic7;
    }
  };

  const showInput = tempType === 1 || tempType === 2 || tempType === 3 || tempType === 4;

  return (
    <div>
      <div className={s.optionsGrid}>
        {/* "Nepasirinkta" (id 0) nebesiūloma kaip pasirinkimas — vartotojas visada
            renkasi realią reikšmę (žr. ClickUp #3); id 0 lieka duomenų modelyje
            kaip numatytoji/dar-nepasirinkta reikšmė (žr. ConfigPanel isPending). */}
        {allBasisOptions.filter((option) => option.id !== 0).map((option) => (
          <SelectionBox
            key={option.id}
            image={option.image}
            title={option.title}
            selected={tempType === option.id}
            type="column"
            onClick={() => handleOptionClick(option.id)}
          />
        ))}
      </div>

      {tempType === 6 && (
        <div className={s.legSection}>
          <Text size={300} weight="semibold" block className={s.legTitle}>
            Pasirinkta kojelė (Aukštis: {tempLeg ? tempLeg.height : '0'}mm):
          </Text>
          <div className={s.legItemWrap}>
            <CellItem
              image={getLegImage()}
              title={tempLeg ? tempLeg.code : 'Pasirinkti kojelę'}
              subTitle={tempLeg ? tempLeg.name : ''}
              selected={!!tempLeg}
              onClick={() => setOpenLegSelectingDialog(true)}
              onView={() => setOpenLegViewDialog(true)}
              viewDisabled={!tempLeg}
            />
          </div>
        </div>
      )}

      {showInput && (
        <div className={s.heightRow}>
          <Text id="basis-height-input">
            Pagrindo aukštis, cm:
          </Text>
          <Input
            type="number"
            size="small"
            className={s.heightInput}
            value={String(Math.round(basisHeight * 100))}
            onChange={handleHeightInputChange}
            contentAfter={cmAdornment}
            min={0}
          />
        </div>
      )}

      <BasisSelectingDialog
        open={openLegSelectingDialog}
        onClose={() => setOpenLegSelectingDialog(false)}
        onItemSelect={handleLegSelect}
        selectedLeg={tempLeg}
      />

      <ItemViewDialog
        open={openLegViewDialog}
        onClose={() => setOpenLegViewDialog(false)}
        item={tempLeg}
        itemType="leg"
      />

      <div className={s.footer}>
        <Button appearance="outline" onClick={onClose}>Uždaryti</Button>
        <Button appearance="primary" onClick={handleConfirm}>Pasirinkti</Button>
      </div>
    </div>
  );
};

export default Basis;
